import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useSetFoundingPartner } from "./use-founding-mutations";

interface ConvertFoundingPartnerDialogProps {
  readonly userId: string;
  /** Hosts (property organizations) get grandfathering; venue-only owners are badge-only. */
  readonly ownsProperties: boolean;
  readonly currentlyPartner: boolean;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onDone: () => void;
}

/**
 * Convert an account to a Founding Partner, or remove the status (ADR-0010
 * §9, ADMIN only). It's a plain confirmation now — there is no chosen
 * grandfather percent: a host convert turns on grandfathering at the ongoing
 * rate, and a venue owner is badge-only. Removing sends `enabled:false` and
 * never touches existing per-property enrollments.
 */
export function ConvertFoundingPartnerDialog({
  userId,
  ownsProperties,
  currentlyPartner,
  open,
  onOpenChange,
  onDone,
}: ConvertFoundingPartnerDialogProps) {
  const { t } = useTranslation();
  const isHost = ownsProperties;
  const [error, setError] = useState<string | null>(null);
  const setPartner = useSetFoundingPartner();

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const canSubmit = !setPartner.isPending;

  const handleClose = (next: boolean) => {
    if (!next && setPartner.isPending) return;
    onOpenChange(next);
  };

  const handleSubmit = () => {
    setError(null);
    setPartner.mutate(
      { userId, enabled: !currentlyPartner },
      {
        onSuccess: () => onDone(),
        onError: (err) => {
          setError(
            resolveMutationErrorMessage(err, t, {
              forbiddenKey: "founding.errors.forbidden",
              serverPrefixKey: "founding.errors.serverPrefix",
              genericKey: "founding.errors.generic",
            }),
          );
        },
      },
    );
  };

  const title = currentlyPartner
    ? t("founding.convert.removeTitle")
    : t("founding.convert.convertTitle");
  const description = currentlyPartner
    ? t("founding.convert.removeDescription")
    : isHost
      ? t("founding.convert.convertHostDescription")
      : t("founding.convert.convertVenueDescription");

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="convert-founding-partner-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!currentlyPartner && !isHost && (
            <p
              data-testid="convert-founding-venue-note"
              className="font-sans text-sm text-muted-foreground"
            >
              {t("founding.convert.venueBadgeOnly")}
            </p>
          )}
          {error && (
            <p data-testid="convert-founding-error" className="font-sans text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={setPartner.isPending}
          >
            {t("founding.convert.cancel")}
          </Button>
          <Button
            type="button"
            variant={currentlyPartner ? "destructive" : "default"}
            data-testid="convert-founding-submit"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {currentlyPartner
              ? t("founding.convert.removeConfirm")
              : t("founding.convert.convertConfirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
